import { Pie } from 'react-chartjs-2';

import { Chart, plugins, elements } from 'chart.js';
import {
  buildChartOptions,
  getTokenColor,
} from 'structure/capital-manager/utils/charts';
import { ChartLegend } from 'structure/capital-manager/components/ChartLedend';
import { useMemo } from 'react';
import { TokenTableRow } from 'structure/capital-manager/types';

// Register necessary components
Chart.register(
  elements.ArcElement,
  elements.LineElement,
  elements.PointElement,
  elements.BarElement,
  plugins.Title,
  plugins.Tooltip,
  plugins.Legend,
  plugins.SubTitle
);

interface AssetsOverviewChartProps {
  tokens: TokenTableRow[];
}

const CUMULATIVE_THRESHOLD = 0.9; // показываем токены пока не наберём 90%
const MIN_ITEMS = 2;
const OTHERS_COLOR = '#6B7280';

export const ChartAssetsOverview = (props: AssetsOverviewChartProps) => {
  const { tokens = [] } = props;

  const groupBySymbol = useMemo(() => {
    return tokens.reduce(
      (acc, token) => {
        if (!acc[token.symbol]) {
          acc[token.symbol] = token.balanceUsd;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [tokens]);

  const allEntries = Object.entries(groupBySymbol)
    .map(([label, value]) => ({ label, value }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = allEntries.reduce((sum, item) => sum + item.value, 0);

  let cumulative = 0;
  let cutIndex = allEntries.length;

  for (let i = 0; i < allEntries.length; i++) {
    cumulative += total > 0 ? allEntries[i].value / total : 0;
    if (cumulative >= CUMULATIVE_THRESHOLD && i + 1 >= MIN_ITEMS) {
      cutIndex = i + 1;
      break;
    }
  }

  const majorEntries = allEntries.slice(0, cutIndex);
  const othersEntries = allEntries.slice(cutIndex);
  const othersValue = othersEntries.reduce((sum, item) => sum + item.value, 0);

  const sortedEntries =
    othersValue > 0 && othersEntries.length > 1
      ? [...majorEntries, { label: 'Others', value: othersValue }]
      : allEntries.slice(0, cutIndex + othersEntries.length);

  const labels = sortedEntries.map((item) => item.label);
  const values = sortedEntries.map((item) => item.value);

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Percentage',
        data: values.map((item) => Number(item.toFixed(2))),
        backgroundColor: labels.map((label, index) =>
          label === 'Others' ? OTHERS_COLOR : getTokenColor(label, index)
        ),
        hoverOffset: 15,
        borderWidth: 0,
      },
    ],
  };

  const options = buildChartOptions({
    title: 'Asset distribution',
  });

  return (
    <div className='flex flex-col items-center gap-4'>
      <Pie options={options} data={data} />
      <ChartLegend labels={labels} dataset={data.datasets[0]} />
    </div>
  );
};
